import { InteractionRequiredAuthError, InteractionStatus, InteractionType } from '@azure/msal-browser'
import { useMsal, useMsalAuthentication } from '@azure/msal-react'
import { isAxiosError } from 'axios'
import { getAuthorityForAccount, getLoginRequest, protectedResources } from '@src/authConfig'
import { AppContext, useAppStore } from '@src/contexts/AppContext'
import { isExpiredToken, parseJwt } from '@src/data/api/api-client'
import useFeatureFlags from '@src/hooks/config-hooks'; // #62287
import { clearUserToken, saveUserToken, signIn } from '@src/services/Authentification'
import { reportErrorToConsole } from '@src/services/error-logger'
import { UserDtoFromJwt } from '@src/types/User'
import { useEffect, type JSX } from 'react'

interface Props {
  children: JSX.Element
}

const UserProvider = ({ children }: Props) => {
  const { instance, accounts, inProgress } = useMsal()
  const { flags } = useFeatureFlags() // #62287
  useMsalAuthentication(InteractionType.Redirect, getLoginRequest(flags))
  const appStore = useAppStore()

  useEffect(() => {
    // #62287 — defense in depth: if a cached account came from the federation policy
    // but the FS is now off, force a logout + redirect to standard policy.

    // Switch merchant detection: when the URL has a targetMerchantId that differs
    // from the cached MSAL account's merchant, expire the local session and trigger
    // a fresh loginRedirect with prompt=login (set by getLoginRequest). clearCache
    // forces MSAL to do a real redirect instead of reusing its cached account.
    // The merchant is read from extension_MerchantsList in the cached token claims,
    // not from appStore.user: on the first mount after navigation appStore.user is
    // still null, so relying on it skipped the mismatch check and reused the previous
    // merchant's cached token (BadRequest when jumping between merchants).
    const targetMerchantId = new URLSearchParams(window.location.search).get('targetMerchantId')
    const cachedClaims = accounts[0]?.idTokenClaims as { extension_MerchantsList?: string } | undefined
    const cachedMerchantId = cachedClaims?.extension_MerchantsList
    const nonMatchingMerchantId = !!cachedMerchantId && cachedMerchantId !== targetMerchantId
    const federationUrlWithAccount = targetMerchantId && accounts[0]
    if (federationUrlWithAccount &&
      (!flags.EnableMerchantDashboardAccessFromMerchants || nonMatchingMerchantId)
    ) {
      const switchMerchant = async () => {
        clearUserToken()
        await instance.clearCache()
        await instance.loginRedirect(getLoginRequest(flags))
      }
      switchMerchant().catch(reportErrorToConsole)
      return
    }

    if (accounts[0] && appStore.user === null && inProgress === InteractionStatus.None) {
      instance.setActiveAccount(accounts[0])
      instance
        .acquireTokenSilent({
          account: accounts[0],
          scopes: protectedResources.getTokenAzure.scopes.read,
          authority: getAuthorityForAccount(accounts[0]),
        })
        .then((response) => {
          return signIn({ jwt: response.idToken }).then(async (r) => {
            const icebergToken = parseJwt(r.jwt) as UserDtoFromJwt
            if (isExpiredToken(icebergToken.exp)) {
              await instance.loginRedirect(getLoginRequest(flags))
              return
            }
            if (!icebergToken.merchantId) {
              await instance.loginRedirect({ ...getLoginRequest(flags), prompt: 'login' })
              return
            }
            saveUserToken(r.jwt)
            appStore.reloadUser()
          })
        })
        .catch((error) => {
          // #65781 — access revoked (disabled/deleted) during the silent re-mint: the exchange
          // returns 403. Clear the session and do a full MSAL logout instead of looping back into
          // acquireTokenSilent (which would otherwise re-mint from the cached account).
          if (isAxiosError(error) && error.response?.status === 403) {
            clearUserToken()
            instance.logoutRedirect({ account: accounts[0] }).catch(reportErrorToConsole)
            return
          }
          if (error instanceof InteractionRequiredAuthError) {
            reportErrorToConsole(new Error('Failed to acquire token silently. Redirecting to login.'))
            instance.loginRedirect(getLoginRequest(flags)).catch(reportErrorToConsole)
          } else {
            reportErrorToConsole(error)
          }
        })
    }
  }, [inProgress, accounts, instance, flags, appStore])

  if (inProgress !== 'none' || !accounts.length || appStore.user === null) return null
  return <AppContext.Provider value={appStore}>{children}</AppContext.Provider>
}

export default UserProvider
